import * as React from 'react';
import { gql } from '@apollo/client';
import get from 'lodash/get';
import {
  ExportButton,
  ExportButtonProps,
  Modal,
  Spinner,
} from '@quality24/design-system';

import {
  ExportConfig,
  GraphQLFilter,
  HasPermissionFn,
} from '../../../../@types';
import { useExportQueryData } from '../../../../hooks/exportDataHook';
import { parseExportData, parseHeaders } from './utils';

const MockQuery = gql`
  query MockQueryNotToBePerformed {
    apiVersion {
      versionName
    }
  }
`;

export interface Props extends React.HTMLAttributes<HTMLElement> {
  filters: GraphQLFilter;
  customQueryVariables?: {
    [keys: string]: GraphQLFilter;
  };
  data?: Record<string, unknown>;
  config: ExportConfig;
  disabled?: boolean;
  hasPermission?: HasPermissionFn;
}

/**
 * <ExportDataButtonContainer> page
 */
export const ExportDataButtonContainer: React.FunctionComponent<Props> = ({
  className,
  filters,
  customQueryVariables = {},
  data,
  config,
  disabled,
  hasPermission,
  ...htmlElementProps
}) => {
  // Filter columns by permissions
  const parsedConfig = React.useMemo(
    () => ({
      ...config,
      columns: config.columns.filter(
        (col) =>
          !hasPermission ||
          !col.permission ||
          (col?.permission && hasPermission(col?.permission)),
      ),
    }),
    [config, hasPermission],
  );

  // Prepare function to build data to export
  const parseData = React.useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (queryData: any) => {
      if (!queryData) return queryData;
      return parseExportData(
        get(queryData, parsedConfig.rootKey, []),
        parsedConfig,
      );
    },
    [parsedConfig],
  );

  // Get export data function
  const { exportData, exportQueryData, loading } = useExportQueryData({
    filename: parsedConfig.filename || 'q24-dashboard',
    query: parsedConfig.query ? gql([parsedConfig.query]) : MockQuery,
    variables: {
      ...customQueryVariables,
      filter: filters,
      clientTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
    parseData,
  });

  // Prepare function to build data to export
  const handleExport = React.useCallback(
    async (type: Parameters<ExportButtonProps['onExport']>[0]) => {
      if (parsedConfig.query) {
        await exportQueryData(type, parseHeaders(parsedConfig));
      } else {
        exportData({
          data: parseData(data),
          type,
          headers: parseHeaders(parsedConfig),
        });
      }
    },
    [exportQueryData, exportData, parseData, data, parsedConfig],
  );

  return (
    <div className={className} {...htmlElementProps}>
      <ExportButton onExport={handleExport} disabled={disabled || loading} />

      <Modal
        id="export-data-modal"
        show={loading}
        closeButton={false}
        style={{ width: 350 }}
      >
        <div className="d-flex flex-column flex-justify-center flex-items-center gap-2">
          <Spinner size="sm" className="p-2" />
          <p>Aguarde enquanto geramos o arquivo com os dados.</p>
        </div>
      </Modal>
    </div>
  );
};

export default ExportDataButtonContainer;
